import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, Banknote, PiggyBank, Target,
  AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, Building2, Clock,
  CheckCircle, Users, Factory, BarChart3, X, ClipboardList
} from "lucide-react";
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { PeriodSelector, DepartmentBadge } from "@/components/UIComponents";
import { formatCurrency, formatPercent, MONTHS, CHART_COLORS, CONFIG } from "@/lib/config";
import api from "@/lib/api";
import { ChantierDuJour } from "@/components/ChantierDuJour";
import { NotificationsAjustement } from "@/components/NotificationsAjustement";
import { EquipesActives } from "@/components/EquipesActives";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { API_URL } from "@/lib/apiUrl";
import { formatLocalTime } from "@/utils/formatLocalTime"; // 🆕 [P1] timezone Québec centralisé
import { CASigneDialog } from "@/components/dashboard/CASigneDialog"; // 📊 Vue d'ensemble V1
import { CASecuriseDialog } from "@/components/dashboard/CASecuriseDialog";
import { RangeeIndicateursCles } from "./dashboard/RangeeIndicateursCles"; // 📊 Vue d'ensemble V2
import { PanneauQuickBooks, useDiagnosticQuickBooks } from "@/components/dashboard/PanneauQuickBooks";

// ── Sub-components ─────────────────────────────────────────────────────────
import { SectionKPIVentes } from "./dashboard/SectionKPIVentes";
import { SectionFinances } from "./dashboard/SectionFinances";
import { SectionTresorerie } from "./dashboard/SectionTresorerie";
import { SectionProductionAvenir } from "./dashboard/SectionProductionAvenir";
import { SectionBTSuivi } from "./dashboard/SectionBTSuivi";
// 🆕 [Phase 96 v2 — 2026-07-09] DataHealthBlock retiré du Dashboard, désormais réutilisé
//     uniquement dans le drill-down du widget Santé Data (page Alertes). Import supprimé
//     du Dashboard pour éviter les composants inutilisés. Le composant reste testé
//     indépendamment (DataHealthBlock.phase84.test.jsx).
import { SectionNotesImportantes } from "./dashboard/SectionNotesImportantes";
import { SectionPaiements } from "./dashboard/SectionPaiements";
import { SectionGraphiques } from "./dashboard/SectionGraphiques";
import { SectionVideosEnApproche } from "./dashboard/SectionVideosEnApproche";
import { SectionInspectionsLocation } from "./dashboard/SectionInspectionsLocation";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const MOIS_COURT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const DEPTS = [
  { key: "PRQC", label: "Peinture PRQC", color: "#059669" },
  { key: "LMDS", label: "LMDS", color: "#2563eb" },
  { key: "LOCATION", label: "Location", color: "#d97706" },
];
const DEPT_COLORS = [CHART_COLORS.prqc || "#059669", CHART_COLORS.lmds || "#2563eb", CHART_COLORS.location || "#d97706"];

export const Dashboard = () => {
  const { user } = useAuth();
  const { canDo } = usePermissions();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [moisProd, setMoisProd] = useState(currentMonth);
  const [yearProd, setYearProd] = useState(currentYear); // Année séparée pour la prod
  const [loading, setLoading] = useState(true);
  const [deptView, setDeptView] = useState('global');
  const [periodView, setPeriodView] = useState('annuel');
  const [quarter, setQuarter] = useState(Math.ceil(currentMonth / 3));

  useEffect(() => {
    const depts = [
      { key: 'global', perm: 'dashboard_dept_global' },
      { key: 'PRQC', perm: 'dashboard_dept_prqc' },
      { key: 'LMDS', perm: 'dashboard_dept_lmds' },
      { key: 'LOCATION', perm: 'dashboard_dept_location' },
    ];
    const allowed = depts.filter(d => canDo(d.perm));
    if (allowed.length > 0 && !allowed.find(d => d.key === deptView)) {
      setDeptView(allowed[0].key);
    }
  }, [canDo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Data states
  const [objectifs, setObjectifs] = useState([]);
  const [ventesYtd, setVentesYtd] = useState(null);
  const [productionMois, setProductionMois] = useState(null);
  const [revenusData, setRevenusData] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [paiementsAttente, setPaiementsAttente] = useState(null);
  const [dossiersProd, setDossiersProd] = useState([]);
  const [dossiersProdEnriched, setDossiersProdEnriched] = useState([]);
  const [dossiersTous, setDossiersTous] = useState([]);
  const [cashflow, setCashflow] = useState(null);
  const [cashSolde, setCashSolde] = useState(0);
  const [debtRatio, setDebtRatio] = useState(null);
  const [loadingProd, setLoadingProd] = useState(false);
  const [projection, setProjection] = useState(null);
  const [backlog, setBacklog] = useState(null);
  const [showCADetail, setShowCADetail] = useState(null);
  const [selectedCAMonth, setSelectedCAMonth] = useState(null);
  const [productionReelle, setProductionReelle] = useState(null);
  const [productionMensuelle, setProductionMensuelle] = useState(null);
  const [productionAnnuelle, setProductionAnnuelle] = useState(null);
  const [productionRealiseeAnn, setProductionRealiseeAnn] = useState(null);
  const [scenario, setScenario] = useState(0);
  const qbDiag = useDiagnosticQuickBooks(estProprietaire(user)); // 📊 V2 : état QuickBooks (propriétaire)
  const [tauxSignatureData, setTauxSignatureData] = useState(null);
  const [kpisRange, setKpisRange] = useState(null);

  const axiosRetry = useCallback(async (url, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const res = await axios.get(url);
        return res.data;
      } catch (e) {
        if (e?.response?.status && e.response.status < 500) return e.response.data || {};
        if (i < retries - 1) await new Promise(r => setTimeout(r, (i + 1) * 1500));
        else throw e;
      }
    }
    return (await axios.get(url)).data;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, revenusRes, cashRes, projRes, prodAnnRes, tauxSigRes, prodRealiseeRes] = await Promise.all([
        axiosRetry(`${API_URL}/api/dashboard/all/${year}/${month}`),
        axiosRetry(`${API_URL}/api/analyse/mensuelle/${year}`),
        axiosRetry(`${API_URL}/api/cash/balance`).catch(() => ({ current_balance: 0 })),
        axiosRetry(`${API_URL}/api/ventes/projection/${year}`).catch(() => null),
        axiosRetry(`${API_URL}/api/production/reelle/annuelle/${year}`).catch(() => null),
        axiosRetry(`${API_URL}/api/dashboard/taux-signature/${year}`).catch(() => null),
        axiosRetry(`${API_URL}/api/production/realisee/annuelle/${year}`).catch(() => null),
      ]);
      setObjectifs(Array.isArray(allRes.objectifs) ? allRes.objectifs : []);
      setVentesYtd(allRes.ventes_ytd || {});
      setRevenusData(Array.isArray(revenusRes) ? revenusRes : []);
      setDashboardData(allRes.dashboard || {});
      setMonthlyData(Array.isArray(revenusRes) ? revenusRes : []);
      setPaiementsAttente(allRes.paiements_attente || {});
      setDossiersTous((allRes.dossiers || {}).dossiers || []);
      setCashflow(allRes.dashboard || {});
      setCashSolde((cashRes || {}).current_balance || 0);
      setProductionMois(allRes.production_ytd);
      setProductionReelle(allRes.production_ytd);
      setProductionMensuelle(allRes.production_mensuelle || null);
      setProjection(projRes);
      setBacklog(allRes.backlog);
      setProductionAnnuelle(prodAnnRes);
      setProductionRealiseeAnn(prodRealiseeRes);
      setTauxSignatureData(tauxSigRes);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year, month, axiosRetry]);

  const loadDossiersProd = useCallback(async (mois, annee) => {
    const y = annee || yearProd;
    setLoadingProd(true);
    // 🆕 [Phase 83 — 2026-06-23] Promise.allSettled au lieu de Promise.all
    // AVANT : un seul reject (ex: 500 sur production-enriched) vidait les 2 tableaux
    // car Promise.all rejette globalement → ni setDossiersProd ni setDossiersProdEnriched
    // n'étaient appelés. Maintenant : chaque endpoint est traité indépendamment.
    const results = await Promise.allSettled([
      axios.get(`${API_URL}/api/crm/ventes/production/${y}/${mois}`),
      axios.get(`${API_URL}/api/crm/ventes/production-enriched/${y}/${mois}`)
    ]);

    if (results[0].status === 'fulfilled') {
      setDossiersProd(results[0].value.data?.dossiers || []);
    } else {
      console.error('[DASHBOARD_FETCH_ERROR] /production:', results[0].reason);
      toast.error('Impossible de charger "BT — suivi des sections" (voir console)');
      setDossiersProd([]);
    }

    if (results[1].status === 'fulfilled') {
      setDossiersProdEnriched(results[1].value.data?.dossiers || []);
    } else {
      console.error('[DASHBOARD_FETCH_ERROR] /production-enriched:', results[1].reason);
      toast.error('Impossible de charger "Production à venir" (voir console)');
      setDossiersProdEnriched([]);
    }

    setLoadingProd(false);
  }, [yearProd]);

  // Auto-naviguer au premier mois avec de la production
  useEffect(() => {
    const autoNav = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/crm/ventes/prochain-mois-production/${currentYear}/${currentMonth}`);
        if (r.data?.nb_bts > 0 && (r.data.mois !== currentMonth || r.data.annee !== currentYear)) {
          setMoisProd(r.data.mois);
          setYearProd(r.data.annee);
          loadDossiersProd(r.data.mois, r.data.annee);
        }
      } catch (e) { /* non bloquant */ }
    };
    autoNav();
  }, [currentYear, currentMonth]);

  useEffect(() => {
    const refYear = new Date().getFullYear() - 1;
    Promise.all([
      axios.get(`${API_URL}/api/configuration/actifs-dettes`).then(r => r.data),
      axios.get(`${API_URL}/api/revente/donnees-annuelles/${refYear}`).then(r => r.data),
    ]).then(([ad, rev]) => {
      const totalMonthlyPayments = (ad.debts || []).reduce((s, d) => s + (parseFloat(d.monthlyPayment) || 0), 0);
      const revenuMensuel = (rev.revenus || 0) / 12;
      setDebtRatio({ totalMonthlyPayments, revenuMensuel, refYear });
    }).catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadDossiersProd(moisProd, yearProd); }, [loadDossiersProd, moisProd, yearProd]);

  // 🆕 Analyse silencieuse des montants fantômes au chargement — toast si détection
  useEffect(() => {
    let cancelled = false;
    const key = `fantomes_last_alert_${year}`;
    const now = Date.now();
    const last = parseInt(localStorage.getItem(key) || '0', 10);
    // Ne pas re-alerter plus d'une fois par 24h
    if (now - last < 24 * 60 * 60 * 1000) return;

    const run = async () => {
      try {
        const res = await api.get(`/api/dashboard/montants-fantomes/${year}`);
        if (cancelled) return;
        const nb = res.data?.nb_fantomes || 0;
        if (nb > 0) {
          const totalHt = res.data?.total_fantome_ht || 0;
          toast.warning(
            `👻 ${nb} montant${nb > 1 ? 's' : ''} fantôme${nb > 1 ? 's' : ''} détecté${nb > 1 ? 's' : ''} (${totalHt.toFixed(2)}$ HT)`,
            {
              description: 'Ouvrez "CA Total Signé YTD" pour les nettoyer.',
              duration: 10000,
            }
          );
        }
        localStorage.setItem(key, String(now));
      } catch (e) {
        // Silencieux, ne pas déranger
      }
    };
    // Délai pour laisser le Dashboard principal charger en premier
    const t = setTimeout(run, 2500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [year]);

  useEffect(() => {
    if (periodView === 'mensuel') { setKpisRange(null); return; }
    const startMonth = periodView === 'trimestre' ? (quarter - 1) * 3 + 1 : 1;
    const endMonth = periodView === 'trimestre' ? quarter * 3 : 12;
    axiosRetry(`${API_URL}/api/dashboard/kpis-range/${year}/${startMonth}/${endMonth}`)
      .then(data => setKpisRange(data?.kpis || null))
      .catch(() => setKpisRange(null));
  }, [year, periodView, quarter, axiosRetry]);

  // ── Calculs (inchangés) ───────────────────────────────────────────────────
  const objCA = {};
  const objProd = {};
  objectifs.forEach(o => {
    if (o.type_objectif === "CA_mensuel") { if (!objCA[o.departement]) objCA[o.departement] = 0; objCA[o.departement] += o.valeur || 0; }
    if (o.type_objectif === "production_mensuel") { if (!objProd[o.departement]) objProd[o.departement] = 0; objProd[o.departement] += o.valeur || 0; }
  });

  const ventesYTD = ventesYtd?.ytd || { PRQC: 0, LMDS: 0, LOCATION: 0, total: 0 };
  const ventesSource = ventesYtd?.source || 'crm';

  const deptKpi = deptView === 'global' ? dashboardData?.kpis?.GLOBAL || {} : dashboardData?.kpis?.[deptView] || {};

  const getFinancePeriod = () => {
    if (periodView === 'mensuel') return { revenus: deptKpi.revenus || {}, depenses: deptKpi.depenses || {}, profits: deptKpi.profits || {}, marges: deptKpi.marges || {}, taxes: deptKpi.taxes || {} };
    if (kpisRange) { const rangeKpi = deptView === 'global' ? (kpisRange.GLOBAL || {}) : (kpisRange[deptView] || {}); return { revenus: rangeKpi.revenus || {}, depenses: rangeKpi.depenses || {}, profits: rangeKpi.profits || {}, marges: rangeKpi.marges || {}, taxes: rangeKpi.taxes || {} }; }
    const months = periodView === 'trimestre' ? revenusData.filter(r => r.mois >= (quarter - 1) * 3 + 1 && r.mois <= quarter * 3) : revenusData;
    if (deptView === 'global') { const totalRev = months.reduce((s, r) => s + (r.revenus || 0), 0); const totalDep = months.reduce((s, r) => s + (r.depenses || 0), 0); const totalProfit = months.reduce((s, r) => s + (r.profit || 0), 0); const margeBrute = totalRev > 0 ? ((totalRev - totalDep) / totalRev * 100) : 0; return { revenus: { total: totalRev, bancaire: totalRev, cash: 0 }, depenses: { total: totalDep }, profits: { brut: totalRev - totalDep, avant_impots: totalProfit, apres_impots: totalProfit }, marges: { brute: margeBrute }, taxes: {} }; }
    const deptRevKey = `revenus_${deptView.toLowerCase()}`; const totalRev = months.reduce((s, r) => s + (r[deptRevKey] || 0), 0);
    return { revenus: { total: totalRev, bancaire: totalRev, cash: 0 }, depenses: deptKpi.depenses || {}, profits: deptKpi.profits || {}, marges: deptKpi.marges || {}, taxes: deptKpi.taxes || {} };
  };
  const periodFinance = getFinancePeriod();
  const deptRevenus = periodFinance.revenus; const deptDepenses = periodFinance.depenses;
  const deptProfits = periodFinance.profits; const deptMarges = periodFinance.marges; const deptTaxes = periodFinance.taxes;

  let productionPlanifieeTotal = deptView === 'global' ? (productionReelle?.total || 0) : (productionReelle?.[deptView] || 0);
  if (periodView === 'trimestre' && Array.isArray(productionAnnuelle)) { const qStart = (quarter - 1) * 3 + 1; const deptKey = deptView === 'global' ? 'total' : deptView; productionPlanifieeTotal = productionAnnuelle.filter(p => p.mois >= qStart && p.mois <= qStart + 2).reduce((s, p) => s + (p[deptKey] || 0), 0); }
  else if (periodView === 'mensuel' && Array.isArray(productionAnnuelle)) { const deptKey = deptView === 'global' ? 'total' : deptView; const moisData = productionAnnuelle.find(p => p.mois === month); productionPlanifieeTotal = moisData ? (moisData[deptKey] || 0) : 0; }

  let productionRealiseeTotal = 0;
  if (Array.isArray(productionRealiseeAnn)) { const deptKey = deptView === 'global' ? 'total' : deptView; if (periodView === 'trimestre') { const qStart = (quarter - 1) * 3 + 1; productionRealiseeTotal = productionRealiseeAnn.filter(p => p.mois >= qStart && p.mois <= qStart + 2).reduce((s, p) => s + (p[deptKey] || 0), 0); } else if (periodView === 'mensuel') { const moisData = productionRealiseeAnn.find(p => p.mois === month); productionRealiseeTotal = moisData ? (moisData[deptKey] || 0) : 0; } else { productionRealiseeTotal = productionRealiseeAnn.reduce((s, p) => s + (p[deptKey] || 0), 0); } }

  const getObjPeriod = (type, dept) => { if (periodView === 'mensuel') return objectifs.filter(o => o.type_objectif === type && o.departement === dept && o.mois === month).reduce((s, o) => s + o.valeur, 0); if (periodView === 'trimestre') { const qS = (quarter - 1) * 3 + 1; return objectifs.filter(o => o.type_objectif === type && o.departement === dept && o.mois >= qS && o.mois <= qS + 2).reduce((s, o) => s + o.valeur, 0); } return objectifs.filter(o => o.type_objectif === type && o.departement === dept).reduce((s, o) => s + o.valeur, 0); };

  const ventesMensuelArr = ventesYtd?.mensuel || [];
  let deptVentesYTD;
  if (deptView === 'LMDS') { deptVentesYTD = productionPlanifieeTotal; }
  else { const ventesAnnuelTotal = deptView === 'global' ? ventesYTD.total : (ventesYTD[deptView] || 0); if (periodView === 'mensuel' && ventesMensuelArr.length > 0) { const moisData = ventesMensuelArr.find(m => m.mois === month); deptVentesYTD = moisData ? (deptView === 'global' ? moisData.total : (moisData[deptView] || 0)) : 0; } else if (periodView === 'trimestre' && ventesMensuelArr.length > 0) { const qS = (quarter - 1) * 3 + 1; deptVentesYTD = ventesMensuelArr.filter(m => m.mois >= qS && m.mois <= qS + 2).reduce((s, m) => s + (deptView === 'global' ? (m.total || 0) : (m[deptView] || 0)), 0); } else { deptVentesYTD = ventesAnnuelTotal; } }

  const deptObjCA = deptView === 'LMDS' ? getObjPeriod('production_mensuel', 'LMDS') : deptView === 'global' ? DEPTS.reduce((s, d) => s + getObjPeriod('CA_mensuel', d.key), 0) : getObjPeriod('CA_mensuel', deptView);
  const deptObjProd = deptView === 'global' ? DEPTS.reduce((s, d) => s + getObjPeriod('production_mensuel', d.key), 0) : getObjPeriod('production_mensuel', deptView);
  const deptPctVentes = deptObjCA > 0 ? (deptVentesYTD / deptObjCA) * 100 : 0;
  const deptBacklogTotal = deptView === 'global' ? (backlog?.total || 0) : (backlog?.[deptView] || 0);
  const deptBacklogDossiers = deptView === 'global' ? (backlog?.dossiers || []) : (backlog?.dossiers || []).filter(d => d.departement === deptView);
  const deptBacklogNb = deptBacklogDossiers.length;
  const filteredPaiements = paiementsAttente?.factures?.filter(f => deptView === 'global' || f.departement === deptView) || [];
  const deptPaiementsTotal = deptView === 'global' ? (paiementsAttente?.total_en_attente || 0) : deptView === 'LMDS' ? (paiementsAttente?.total_lmds || 0) : filteredPaiements.reduce((s, f) => s + (f.solde_du || 0), 0);
  const deptPaiementsNb = deptView === 'global' ? (paiementsAttente?.nb_factures || 0) : deptView === 'LMDS' ? ((paiementsAttente?.lmds_rapports?.length || 0) + (paiementsAttente?.lmds_factures?.length || 0)) : filteredPaiements.length;
  const periodLabel = periodView === 'mensuel' ? MONTHS[month] + ' ' + year : periodView === 'trimestre' ? `T${quarter} ${year}` : String(year);

  const ventesSignees = deptVentesYTD;
  const getRevenusEncaissesPeriod = () => { const deptRevKey = deptView === 'global' ? 'revenus' : `revenus_${deptView.toLowerCase()}`; if (periodView === 'mensuel') { const m = revenusData.find(r => r.mois === month); return m ? (m[deptRevKey] || 0) : 0; } else if (periodView === 'trimestre') { const qS = (quarter - 1) * 3 + 1; return revenusData.filter(r => r.mois >= qS && r.mois <= qS + 2).reduce((s, r) => s + (r[deptRevKey] || 0), 0); } return revenusData.reduce((s, r) => s + (r[deptRevKey] || 0), 0); };
  const revenusEncaisses = getRevenusEncaissesPeriod();
  const filteredDossiersProd = dossiersProd?.filter(d => deptView === 'global' || d.departement === deptView) || [];
  const filteredDossiersProdEnriched = dossiersProdEnriched?.filter(d => deptView === 'global' || d.departement === deptView) || [];

  const globalKpi = dashboardData?.kpis?.GLOBAL || {};
  const taxes = globalKpi.taxes || {};
  const kpiSource = (periodView !== 'mensuel' && kpisRange) ? kpisRange : dashboardData?.kpis;
  const deptData = kpiSource ? CONFIG.departments.map((dept, i) => ({ name: CONFIG.department_names?.[dept] || dept, value: kpiSource[dept]?.revenus?.total || 0, dept })).filter(d => d.value > 0) : [];

  const chartVentes = MOIS_COURT.map((m, i) => { const moisNum = i + 1; const vMois = ventesYtd?.mensuel?.find(v => v.mois === moisNum); const rMois = revenusData.find(r => r.mois === moisNum); const objMois = objectifs.filter(o => o.type_objectif === "CA_mensuel" && o.mois === moisNum).reduce((s, o) => s + o.valeur, 0); return { mois: m, ventes: deptView === 'global' ? (vMois?.total || 0) : (vMois?.[deptView] || 0), revenus: rMois?.revenus || 0, objectif: objMois }; });

  // ── RENDER ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-6 max-w-7xl mx-auto">

      {/* Widget pointage unifié (mobile) — ChantierDuJour gère BT standard, BT interne, pause, fallback */}
      <div className="md:hidden"><ChantierDuJour /></div>

      {/* Notifications d'ajustement */}
      {canDo('dashboard_ajustements') && <NotificationsAjustement cpId={user?.employee_id} />}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <span className="truncate">Vue d&apos;ensemble</span>
          </h1>
          <p className="text-gray-500 mt-0.5 text-xs sm:text-sm">{periodView === 'mensuel' ? `${MONTHS[month]} ${year}` : periodView === 'trimestre' ? `T${quarter} ${year}` : String(year)}</p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          {/* Navigation année — haut droite */}
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setYear(y => y - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24 h-8 bg-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2027, 2026, 2025, 2024, 2023].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setYear(y => y + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={loadData}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Sélecteurs département + période */}
      <div className="flex flex-wrap gap-3">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="dept-selector">
          {[{ key: 'global', label: 'Global', perm: 'dashboard_dept_global' }, { key: 'PRQC', label: 'PRQC', perm: 'dashboard_dept_prqc' }, { key: 'LMDS', label: 'LMDS', perm: 'dashboard_dept_lmds' }, { key: 'LOCATION', label: 'Location', perm: 'dashboard_dept_location' }].filter(d => canDo(d.perm)).map(d => (
            <button key={d.key} onClick={() => setDeptView(d.key)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${deptView === d.key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>{d.label}</button>
          ))}
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="period-selector">
          {[{ key: 'mensuel', label: 'Mois' }, { key: 'trimestre', label: 'Trimestre' }, { key: 'annuel', label: 'Annee' }].map(p => (
            <button key={p.key} onClick={() => setPeriodView(p.key)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${periodView === p.key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>{p.label}</button>
          ))}
        </div>
        {/* Boutons Trimestres */}
        {periodView === 'trimestre' && (
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="quarter-selector">
            {[1, 2, 3, 4].map(q => (
              <button key={q} onClick={() => setQuarter(q)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${quarter === q ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>T{q}</button>
            ))}
          </div>
        )}
        {/* Boutons Mois inline */}
        {periodView === 'mensuel' && (
          <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-lg" data-testid="month-selector-inline">
            {['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'].map((m, idx) => (
              <button key={idx + 1} onClick={() => setMonth(idx + 1)} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${month === idx + 1 ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>{m}</button>
            ))}
          </div>
        )}
      </div>

      {/* Avertissement aucune donnée financière — Cat H : bannière destinée à l'Owner uniquement (contexte QuickBooks / setup). */}
      {estProprietaire(user) && dashboardData && (dashboardData.transactions_count === 0 || dashboardData.valid_transactions === 0) && (
        <div data-testid="no-data-warning" className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Aucune transaction QuickBooks pour {MONTHS[month]} {year}</p>
            <p className="text-xs text-amber-600 mt-0.5">Les KPI financiers (revenus, dépenses, taxes) afficheront 0$. Les données importées couvrent 2024 et 2025.</p>
          </div>
        </div>
      )}

      {/* ─── 📊 V2 : panneau d'état QuickBooks (propriétaire) + rangée d'indicateurs clés ─── */}
      {estProprietaire(user) && <PanneauQuickBooks diag={qbDiag} />}
      <RangeeIndicateursCles
        year={year} periodLabel={periodLabel} periodView={periodView} deptView={deptView}
        caSigne={deptVentesYTD} caSecurise={deptBacklogTotal} productionRealisee={productionRealiseeTotal}
        encaissements={revenusEncaisses} paiementsAttente={deptPaiementsTotal} qbDiag={qbDiag}
        onCASigne={() => setShowCADetail('ytd')} onCASecurise={() => setShowCADetail('backlog')} canDo={canDo}
      />

      {/* ─── Section 1: KPIs Ventes ─── */}
      {canDo('dashboard_ventes') && (
        <SectionKPIVentes
          deptView={deptView} periodLabel={periodLabel} periodView={periodView}
          month={month} quarter={quarter} year={year}
          deptVentesYTD={deptVentesYTD} deptObjCA={deptObjCA} deptPctVentes={deptPctVentes}
          deptBacklogTotal={deptBacklogTotal} deptBacklogNb={deptBacklogNb}
          deptPaiementsTotal={deptPaiementsTotal} deptPaiementsNb={deptPaiementsNb}
          paiementsAttente={paiementsAttente}
          ventesSource={ventesSource} setShowCADetail={setShowCADetail}
          objectifs={objectifs} ventesYtd={ventesYtd} ventesYTD={ventesYTD}
          productionMois={productionMois} productionMensuelle={productionMensuelle}
          productionAnnuelle={productionAnnuelle} productionRealiseeAnn={productionRealiseeAnn}
          revenusData={revenusData}
          DEPTS={DEPTS} MONTHS={MONTHS} MOIS_COURT={MOIS_COURT}
          canDo={canDo}
        />
      )}

      {/* ─── Section 2: Finances ─── */}
      <SectionFinances
        deptView={deptView} periodLabel={periodLabel} periodView={periodView} month={month} year={year}
        deptRevenus={deptRevenus} deptDepenses={deptDepenses} deptProfits={deptProfits}
        deptMarges={deptMarges} deptTaxes={deptTaxes}
        ventesSignees={ventesSignees} revenusEncaisses={revenusEncaisses}
        productionPlanifieeTotal={productionPlanifieeTotal} productionRealiseeTotal={productionRealiseeTotal}
        deptObjProd={deptObjProd} MONTHS={MONTHS} canDo={canDo} qbDiag={qbDiag}
      />

      {/* ─── Section 3: Trésorerie — 📊 V2 : visible dans toutes les vues, mention « vue globale seulement » hors Global ─── */}
      {canDo('dashboard_tresorerie') && (
        <SectionTresorerie cashflow={cashflow} cashSolde={cashSolde} taxes={taxes} debtRatio={debtRatio} deptView={deptView} qbDiag={qbDiag} />
      )}

      {/* ─── Équipes actives ─── */}
      {canDo('dashboard_equipes_actives') && deptView !== 'LMDS' && deptView !== 'LOCATION' && deptView !== 'global' && <EquipesActives />}

      {/* ─── Section 4: Production à venir + Paiements + BT Suivi ─── */}
      {canDo('dashboard_production') && (
        <>
        {/* ─── Notes importantes — section dédiée ─── */}
        {(deptView !== 'LMDS' && deptView !== 'LOCATION' && deptView !== 'global') && (
          <SectionNotesImportantes
            filteredDossiersProd={filteredDossiersProd}
            filteredDossiersProdEnriched={filteredDossiersProdEnriched}
            moisProd={moisProd}
            yearProd={yearProd}
            MONTHS={MONTHS}
          />
        )}
        <div className={`grid grid-cols-1 ${deptView === 'LMDS' ? '' : 'md:grid-cols-2'} gap-6`}>
          {deptView !== 'LMDS' && deptView !== 'LOCATION' && deptView !== 'global' && (
            <SectionProductionAvenir
              deptView={deptView} year={yearProd} moisProd={moisProd}
              setMoisProd={(fn) => {
                const newMois = typeof fn === 'function' ? fn(moisProd) : fn;
                // Gérer la transition d'année
                if (newMois > 12) { setYearProd(y => y + 1); setMoisProd(1); }
                else if (newMois < 1) { setYearProd(y => y - 1); setMoisProd(12); }
                else { setMoisProd(newMois); }
              }}
              loadingProd={loadingProd} filteredDossiersProd={filteredDossiersProd}
              filteredDossiersProdEnriched={filteredDossiersProdEnriched}
              canDo={canDo} navigate={navigate}
              userRole={user?.role}
              onReloadRequested={() => loadDossiersProd(moisProd, yearProd)}
            />
          )}
          {(canDo('dashboard_production') || canDo('dashboard_ventes')) && deptView !== 'LMDS' && deptView !== 'LOCATION' && deptView !== 'global' && (
            <SectionBTSuivi filteredDossiersProd={filteredDossiersProd} navigate={navigate} />
          )}
          {canDo('widget_videos_en_approche') && deptView !== 'LMDS' && deptView !== 'LOCATION' && deptView !== 'global' && (
            <SectionVideosEnApproche deptView={deptView} />
          )}
          <SectionPaiements
            deptView={deptView} deptPaiementsTotal={deptPaiementsTotal} deptPaiementsNb={deptPaiementsNb}
            paiementsAttente={paiementsAttente} filteredPaiements={filteredPaiements}
            month={month} year={year} navigate={navigate} MONTHS={MONTHS}
          />
        </div>
        </>
      )}

      {/* 🆕 [Phase 96 v2 — 2026-07-09] DataHealthBlock déplacé vers le widget Santé Data
          (page Alertes). Le composant reste importé et est réutilisé dans le drill-down
          du badge « BT orphelins » du SanteDataWidget. */}

      {/* ─── Section Inspections Location (Vue d'ensemble Location ou Global) ─── */}
      {(deptView === 'LOCATION' || deptView === 'global') && (
        <SectionInspectionsLocation navigate={navigate} />
      )}

      {/* ─── Section 5: Projection fin d'année ─── */}
      {canDo('dashboard_projections') && projection && (
        <SectionProjectionWrapper
          deptView={deptView} year={year} periodView={periodView} month={month} quarter={quarter}
          projection={projection} objectifs={objectifs} productionAnnuelle={productionAnnuelle}
          productionRealiseeAnn={productionRealiseeAnn} backlog={backlog}
          scenario={scenario} setScenario={setScenario}
          navigate={navigate} MONTHS={MONTHS}
          ventesYtd={ventesYtd} ventesYTD={ventesYTD} revenusData={revenusData}
        />
      )}

      {/* ─── Sections spécifiques LMDS ─── */}
      {deptView === 'LMDS' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-200">
            <CardContent className="pt-4 pb-3 px-4 text-center">
              <p className="text-xs text-blue-600 font-semibold uppercase">Paiements installateurs</p>
              <p className="text-xl font-bold text-blue-800 mt-1">{formatCurrency(paiementsAttente?.total_lmds || 0)}</p>
              <p className="text-xs text-gray-500">Rapports: {formatCurrency(paiementsAttente?.total_lmds_rapports || 0)} | Factures: {formatCurrency(paiementsAttente?.total_lmds_factures || 0)}</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/sous-traitants/lmds-non-payees')}>
            <CardContent className="pt-4 pb-3 px-4 text-center">
              <p className="text-xs text-red-600 font-semibold uppercase">Installations non payees</p>
              <p className="text-xl font-bold text-red-700 mt-1">{paiementsAttente?.non_payees_count || 0}</p>
              {(paiementsAttente?.non_payees_detail || []).length > 0 ? (
                <div className="text-xs text-gray-500 mt-1">
                  {paiementsAttente.non_payees_detail.map((d, i) => (
                    <span key={d.id || d._id || i}>{d.installateur_nom}: {d.count}{i < paiementsAttente.non_payees_detail.length - 1 ? ' | ' : ''}</span>
                  ))}
                </div>
              ) : <p className="text-xs text-gray-500">Cliquer pour voir le detail</p>}
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="pt-4 pb-3 px-4 text-center">
              <p className="text-xs text-emerald-600 font-semibold uppercase">Facturation mensuelle</p>
              <p className="text-xl font-bold text-emerald-800 mt-1">{formatCurrency(paiementsAttente?.facturation_mensuelle || 0)}</p>
              <p className="text-xs text-gray-500">{MONTHS[month]} {year} (part entreprise)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Section 6: Taux de signature + Graphiques ─── */}
      <SectionGraphiques
        deptView={deptView} year={year} month={month} periodView={periodView}
        chartVentes={chartVentes} deptObjCA={deptObjCA} monthlyData={monthlyData}
        deptData={deptData} tauxSignatureData={tauxSignatureData}
        MONTHS={MONTHS} CHART_COLORS={CHART_COLORS} DEPT_COLORS={DEPT_COLORS}
        canDo={canDo}
      />

      {/* ─── Popup détail CA ─── 📊 V1 : nouveaux dialogues (recherche, vue mois/client, composition) ; l'ancien CADetailPopup reste disponible via showCADetail === 'legacy' */}
      {showCADetail === 'ytd' && <CASigneDialog annee={year} deptView={deptView} onFermer={() => { setShowCADetail(null); setSelectedCAMonth(null); }} />}
      {showCADetail === 'backlog' && <CASecuriseDialog annee={year} deptInitial={deptView} onFermer={() => { setShowCADetail(null); setSelectedCAMonth(null); }} />}
      {showCADetail && !['ytd', 'backlog'].includes(showCADetail) && (
        <CADetailPopup
          showCADetail={showCADetail} setShowCADetail={setShowCADetail}
          selectedCAMonth={selectedCAMonth} setSelectedCAMonth={setSelectedCAMonth}
          deptView={deptView} periodView={periodView} periodLabel={periodLabel}
          year={year} month={month} quarter={quarter}
          deptVentesYTD={deptVentesYTD} ventesYtd={ventesYtd} ventesYTD={ventesYTD}
          ventesSource={ventesSource} backlog={backlog}
          navigate={navigate} MONTHS={MONTHS}
        />
      )}
    </div>
  );
};

// ── Projection wrapper (keeps the complex IIFE logic inline) ────────────────
// This is kept in the same file to avoid moving the 240-line IIFE into a separate file
// while still keeping Dashboard clean. The original logic is 100% preserved.
function SectionProjectionWrapper(props) {
  // This component just re-exports the original projection rendering logic
  // It receives all needed data as props and renders identically
  return <ProjectionContent {...props} />;
}

// Keeping original projection + CA detail as inline components
// to avoid breaking the complex IIFE logic

function ProjectionContent({ deptView, year, projection, objectifs, productionAnnuelle, productionRealiseeAnn, scenario, setScenario, navigate, MONTHS }) {
  const isLMDS = deptView === 'LMDS';
  const isLocation = deptView === 'LOCATION';
  const useProductionProjection = isLMDS || isLocation;
  const MOIS_NOMS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

  let projData;
  if (useProductionProjection) {
    const cm = projection.current_month || (new Date().getMonth() + 1);
    const completedMonths = cm - 1;
    const deptKey = isLMDS ? 'LMDS' : 'LOCATION';
    const objMensuels = {}; let objAnnuel = 0;
    objectifs.filter(o => o.type_objectif === 'production_mensuel' && o.departement === deptKey).forEach(o => { objMensuels[o.mois] = (o.valeur || 0); objAnnuel += (o.valeur || 0); });
    const reelParMois = {}; let ytd = 0; let objCompleted = 0;
    for (let m = 1; m <= 12; m++) { const val = Array.isArray(productionAnnuelle) ? (productionAnnuelle.find(p => p.mois === m)?.[deptKey] || 0) : 0; reelParMois[m] = val; if (m < cm) { ytd += val; objCompleted += (objMensuels[m] || 0); } }
    const reelMoisCourant = reelParMois[cm] || 0; ytd += reelMoisCourant;
    const perfRatio = objCompleted > 0 && (ytd - reelMoisCourant) > 0 ? (ytd - reelMoisCourant) / objCompleted : 1;
    const dayOfMonth = new Date().getDate(); const daysInMonth = new Date(new Date().getFullYear(), cm, 0).getDate();
    const prorataMoisCourant = reelMoisCourant > 0 ? dayOfMonth / daysInMonth : 0;
    const moisEffectifs = completedMonths + prorataMoisCourant; const avg = moisEffectifs > 0 ? ytd / moisEffectifs : 0;
    const projMoisCourant = (objMensuels[cm] || 0) * perfRatio;
    const prodMonthly = MOIS_NOMS.map((nom, i) => { const m = i + 1; const reel = reelParMois[m] || 0; const objMois = objMensuels[m] || 0; const proj = objMois * perfRatio; if (m < cm) { return { mois_nom: nom, valeur: reel, projection: Math.max(0, proj - reel), est_reel: true, est_projection: false, est_courant: false }; } else if (m === cm) { return { mois_nom: nom, valeur: reelMoisCourant, projection: Math.max(0, proj - reelMoisCourant), est_reel: reelMoisCourant > 0, est_projection: true, est_courant: true }; } else { return { mois_nom: nom, valeur: 0, projection: Math.round(proj * 100) / 100, est_reel: false, est_projection: true, est_courant: false }; } });
    const projAnnuelle = ytd + (projMoisCourant - reelMoisCourant > 0 ? projMoisCourant - reelMoisCourant : 0) + prodMonthly.filter(p => !p.est_reel && !p.est_courant && p.est_projection).reduce((s, p) => s + p.projection, 0);
    const pctObj = objAnnuel > 0 ? Math.round(projAnnuelle / objAnnuel * 100 * 10) / 10 : null;
    projData = { ...projection, ytd_total: ytd, avg_monthly: avg, projection_annuelle: projAnnuelle, objectif_annuel: objAnnuel, pct_objectif: pctObj, monthly: prodMonthly, perf_ratio: perfRatio, has_stacked: true };
  } else { projData = projection; }

  const { projection_annuelle, objectif_annuel, ytd_total, pct_objectif, monthly, backlog_next_year, avg_monthly, current_month: cm } = projData;
  const projLabel = isLMDS ? 'Projection de production' : isLocation ? 'Projection Location' : 'Projection des ventes';
  const isOnTrack = pct_objectif >= 90; const isWarning = pct_objectif >= 70 && pct_objectif < 90;
  const trackColor = isOnTrack ? "#059669" : isWarning ? "#d97706" : "#dc2626";
  const trackBg = isOnTrack ? "bg-emerald-50 border-emerald-200" : isWarning ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";
  const moisRestants = 12 - (cm || 0);
  // 📊 V2 : 3 barres côte à côte (Objectif / Prévision / Réel) ; ±X % (registre projection_scenario_pct) appliqué aux barres Prévision.
  const pctScen = Number(projection?.scenario_pct ?? 20);
  const objParMois = {};
  objectifs.filter(o => useProductionProjection ? (o.type_objectif === 'production_mensuel' && o.departement === deptView) : (o.type_objectif === 'CA_mensuel' && (deptView === 'global' || o.departement === deptView))).forEach(o => { objParMois[o.mois] = (objParMois[o.mois] || 0) + (o.valeur || 0); });
  const barres3 = (monthly || []).map((m, i) => {
    const reel = m.valeur || 0; const futur = m.est_projection;
    const prevision = futur ? Math.round((reel + (m.projection || 0)) * (1 + scenario / 100) * 100) / 100 : reel;
    return { mois_nom: m.mois_nom, Objectif: objParMois[i + 1] || 0, Prévision: prevision, Réel: reel, est_courant: m.est_courant };
  });
  const explication = projection?.explication || `Prévision = rythme réel/objectif appliqué aux mois restants, ±${pctScen} % selon le scénario`;

  return (
    <Card className={`border ${trackBg}`} data-testid="projection-ventes-widget">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: trackColor }} />
              {projLabel} {deptView !== 'global' ? deptView + ' ' : ''}— fin {year} <span className="text-xs font-normal text-slate-500">(année civile {year})</span>
            </CardTitle>
            <CardDescription>
              {useProductionProjection && projData.perf_ratio ? `Reel YTD + projection basee sur tendance (${Math.round(projData.perf_ratio * 100)}% des objectifs) pour les ${moisRestants} mois restants` : `Reel YTD + extrapolation cadence moyenne pour les ${moisRestants} mois restants`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-white border rounded-lg p-0.5">
              {[{ val: -pctScen, label: `Pessimiste -${pctScen}%`, color: "text-red-700 bg-red-50 border-red-200" }, { val: 0, label: "Réaliste", color: "text-emerald-700 bg-emerald-50 border-emerald-200" }, { val: pctScen, label: `Optimiste +${pctScen}%`, color: "text-blue-700 bg-blue-50 border-blue-200" }].map(s => (
                <button key={s.val} onClick={() => setScenario(s.val)} data-testid={`scenario-btn-${s.val < 0 ? '-20' : s.val > 0 ? '20' : '0'}`} className={`text-xs px-2 py-1 rounded-md transition-all font-medium border ${scenario === s.val ? s.color : 'text-gray-400 border-transparent'}`}>{s.label}</button>
              ))}
            </div>
            {pct_objectif && (
              <div className="text-right">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: trackColor }}>{isOnTrack ? "En bonne trajectoire" : isWarning ? "À surveiller" : "En retard sur l'objectif"}</p>
                <p className="text-2xl font-bold" style={{ color: trackColor }}>{scenario !== 0 ? `${(projection_annuelle * (1 + scenario / 100) / objectif_annuel * 100).toFixed(1)}%` : `${pct_objectif}%`}</p>
                <p className="text-xs text-gray-500">de l'objectif annuel {scenario !== 0 ? `(scén. ${scenario > 0 ? '+' : ''}${scenario}%)` : ''}</p>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="text-center p-2 bg-white rounded-lg border"><p className="text-xs text-gray-500 mb-0.5">{isLMDS || isLocation ? 'Produit YTD' : 'Signe YTD'}</p><p className="text-base font-bold text-gray-900">{formatCurrency(ytd_total)}</p></div>
          <div className="text-center p-2 bg-white rounded-lg border"><p className="text-xs text-gray-500 mb-0.5">Moy. mensuelle</p><p className="text-base font-bold text-gray-900">{formatCurrency(avg_monthly)}</p></div>
          <div className="text-center p-2 rounded-lg border" style={{ backgroundColor: `${trackColor}15`, borderColor: `${trackColor}40` }}><p className="text-xs text-gray-500 mb-0.5">Projeté {scenario !== 0 ? `(${scenario > 0 ? '+' : ''}${scenario}%)` : 'annuel'}</p><p className="text-base font-bold" style={{ color: trackColor }}>{formatCurrency(projection_annuelle * (1 + scenario / 100))}</p></div>
          <div className="text-center p-2 bg-white rounded-lg border"><p className="text-xs text-gray-500 mb-0.5">Objectif annuel</p><p className="text-base font-bold text-gray-900">{objectif_annuel > 0 ? formatCurrency(objectif_annuel) : '—'}</p></div>
        </div>
        {objectif_annuel > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs mb-1 text-gray-600"><span>Projection vs Objectif</span><span>{formatCurrency(projection_annuelle)} / {formatCurrency(objectif_annuel)}</span></div>
            <div className="w-full bg-gray-100 rounded-full h-3 relative">
              <div className="h-3 rounded-l-full bg-emerald-500 absolute top-0 left-0 transition-all" style={{ width: `${Math.min(100, (ytd_total / objectif_annuel) * 100)}%` }} />
              <div className="h-3 rounded-full absolute top-0 left-0 transition-all opacity-40" style={{ width: `${Math.min(100, pct_objectif)}%`, backgroundColor: trackColor }} />
            </div>
            <div className="flex justify-between text-xs mt-0.5 text-gray-400"><span>Réel : {((ytd_total / objectif_annuel) * 100).toFixed(0)}%</span><span>Projection : {pct_objectif}%</span></div>
          </div>
        )}
        <p className="text-xs text-slate-600 mb-2 italic" data-testid="projection-explication">{explication}{scenario !== 0 ? ` — scénario appliqué : ${scenario > 0 ? '+' : ''}${scenario} % sur les barres Prévision` : ''}</p>
        <div className="h-48" data-testid="projection-chart-3-barres">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barres3} barSize={10} barGap={2}>
              <XAxis dataKey="mois_nom" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v === 0 ? '' : `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, name) => [formatCurrency(v), name === 'Réel' ? 'Réel = contrats signés + ajouts acceptés du mois' : name]} labelFormatter={l => `${l} ${year}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Objectif" fill="#94a3b8" />
              <Bar dataKey="Prévision" fill="#3b82f6">{barres3.map((e, i) => <Cell key={`p-${i}`} fill={e.est_courant ? '#1d4ed8' : '#3b82f6'} />)}</Bar>
              <Bar dataKey="Réel" fill="#059669" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 justify-center">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-400 inline-block" /> Objectif mensuel</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500 inline-block" /> Prévision (réel + rythme, ±{pctScen} %)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-600 inline-block" /> {isLMDS || isLocation ? 'Production réelle' : 'Ventes réelles'}</span>
        </div>
        {backlog_next_year?.total > 0 && (
          <div className="mt-3 pt-3 border-t flex items-center justify-between gap-3 flex-wrap">
            <div><p className="text-xs font-semibold text-purple-700">Backlog {backlog_next_year.annee} — {backlog_next_year.nb_dossiers} contrat(s) déjà signé(s) pour l'année prochaine</p><p className="text-xs text-gray-500 mt-0.5">Contrats signés en {year} avec chantier planifié en {backlog_next_year.annee}</p></div>
            <p className="text-xl font-bold text-purple-700">{formatCurrency(backlog_next_year.total)}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── CA Detail Popup ── (kept in same file to preserve navigate/state access)
function CADetailPopup({ showCADetail, setShowCADetail, selectedCAMonth, setSelectedCAMonth, deptView, periodView, periodLabel, year, month, quarter, deptVentesYTD, ventesYtd, ventesYTD, ventesSource, backlog, navigate, MONTHS }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowCADetail(null)}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
          <div>
            <h2 className="font-bold text-lg">{showCADetail === 'ytd' ? `CA Total Signe ${periodView === 'annuel' ? 'YTD' : periodLabel} ${deptView !== 'global' ? deptView + ' ' : ''}` : `CA Securise${deptView === 'LMDS' ? ' pour l\'entreprise' : ''} ${deptView !== 'global' ? deptView : ''}`}</h2>
            {showCADetail === 'ytd' && selectedCAMonth && <button onClick={() => setSelectedCAMonth(null)} className="text-xs text-blue-600 hover:underline">← Retour aux mois</button>}
          </div>
          <button onClick={() => { setShowCADetail(null); setSelectedCAMonth(null); }} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          {showCADetail === 'ytd' ? (
            deptView === 'LMDS' ? (
              <LMDSProductionDetail year={year} selectedCAMonth={selectedCAMonth} setSelectedCAMonth={setSelectedCAMonth} navigate={navigate} onClose={() => { setShowCADetail(null); setSelectedCAMonth(null); }} />
            ) : selectedCAMonth ? (
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-slate-700 mb-2">{MONTHS[selectedCAMonth]} {year} — Détail par client</h3>
                {ventesYtd?.clients_par_mois?.[selectedCAMonth]?.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b bg-slate-50"><th className="text-left py-2 px-2">Client</th><th className="text-left py-2">Dept</th><th className="text-right py-2 px-2">Montant</th></tr></thead>
                    <tbody>{ventesYtd.clients_par_mois[selectedCAMonth].filter(c => deptView === 'global' || c.departement === deptView).map((c, i) => (
                      <tr key={c.id || c._id || i} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => { setShowCADetail(null); setSelectedCAMonth(null); navigate(`/crm/projets/${c.dossier_id || c.id}`); }}>
                        <td className="py-2 px-2 text-blue-700">{c.client_nom}</td><td className="py-2 text-xs">{c.departement}</td><td className="py-2 px-2 text-right font-bold">{formatCurrency(c.montant)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <p className="text-sm text-slate-400 italic">Aucun client pour ce mois.</p>}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">Cliquez sur un mois pour voir le détail par client</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50"><th className="text-left py-2 px-2">Mois</th>{deptView === 'global' && <><th className="text-right py-2">PRQC</th><th className="text-right py-2">LMDS</th><th className="text-right py-2">Location</th></>}<th className="text-right py-2 px-2">Total</th></tr></thead>
                  <tbody>{ventesYtd?.mensuel?.filter(m => { if (periodView === 'mensuel' && m.mois !== month) return false; if (periodView === 'trimestre') { const qS = (quarter - 1) * 3 + 1; if (m.mois < qS || m.mois > qS + 2) return false; } return (deptView === 'global' ? m.total : (m[deptView] || 0)) > 0; }).map(m => (
                    <tr key={m.mois} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedCAMonth(m.mois)}>
                      <td className="py-2 px-2 text-blue-700 font-medium">{MONTHS[m.mois]}</td>
                      {deptView === 'global' && <><td className="text-right py-2">{formatCurrency(m.PRQC)}</td><td className="text-right py-2">{formatCurrency(m.LMDS)}</td><td className="text-right py-2">{formatCurrency(m.LOCATION)}</td></>}
                      <td className="text-right py-2 px-2 font-bold">{formatCurrency(deptView === 'global' ? m.total : (m[deptView] || 0))}</td>
                    </tr>
                  ))}</tbody>
                  <tfoot><tr className="font-bold border-t-2"><td className="py-2 px-2">Total</td>{deptView === 'global' && <><td className="text-right">{formatCurrency(ventesYTD.PRQC)}</td><td className="text-right">{formatCurrency(ventesYTD.LMDS)}</td><td className="text-right">{formatCurrency(ventesYTD.LOCATION)}</td></>}<td className="text-right px-2 text-emerald-700">{formatCurrency(deptVentesYTD)}</td></tr></tfoot>
                </table>
                <p className="text-xs text-slate-400">Source: {ventesSource === 'crm' ? 'Contrats signés (CRM)' : 'Revenus QuickBooks'}</p>

                {/* 🔧 Diagnostic : détecter les contrats "Accepté/En attente" non comptés */}
                <DiagnosticCASigneCard year={year} navigate={navigate} onClose={() => { setShowCADetail(null); setSelectedCAMonth(null); }} />
              </div>
            )
          ) : (
            deptView === 'LMDS' ? (
              <LMDSBacklogDetail backlog={backlog} selectedCAMonth={selectedCAMonth} setSelectedCAMonth={setSelectedCAMonth} navigate={navigate} onClose={() => { setShowCADetail(null); setSelectedCAMonth(null); }} />
            ) : (
              <div className="space-y-2">
                {selectedCAMonth ? (
                  /* Vue détail d'un mois */
                  <div>
                    <button onClick={() => setSelectedCAMonth(null)} className="text-xs text-purple-600 hover:underline mb-3 flex items-center gap-1">← Retour aux mois</button>
                    <h3 className="font-semibold text-sm text-slate-700 mb-2">
                      {backlog?.par_mois?.[selectedCAMonth]?.label || selectedCAMonth} — Contrats
                    </h3>
                    {backlog?.dossiers?.filter(d => (deptView === 'global' || d.departement === deptView) && d.mois_cle === selectedCAMonth).length > 0 ? (
                      <table className="w-full text-sm">
                        <thead><tr className="border-b bg-slate-50">
                          <th className="text-left py-2 px-2">Client</th>
                          <th className="text-left py-2">Dept</th>
                          <th className="text-left py-2 text-xs text-slate-400">Statut</th>
                          <th className="text-right py-2 px-2">Montant</th>
                        </tr></thead>
                        <tbody>
                          {backlog.dossiers
                            .filter(d => (deptView === 'global' || d.departement === deptView) && d.mois_cle === selectedCAMonth)
                            .map((d, i) => (
                              <tr key={d.id || d._id || i} className="border-b cursor-pointer hover:bg-purple-50"
                                onClick={() => { setShowCADetail(null); setSelectedCAMonth(null); navigate(`/crm/projets/${d.id}`); }}>
                                <td className="py-2 px-2 text-purple-700">{d.client_nom}</td>
                                <td className="py-2 text-xs">{d.departement}</td>
                                <td className="py-2 text-xs text-slate-400">{d.statut}</td>
                                <td className="py-2 px-2 text-right font-bold">{formatCurrency(d.montant)}</td>
                              </tr>
                            ))
                          }
                        </tbody>
                        <tfoot>
                          <tr className="font-bold border-t-2">
                            <td className="py-2 px-2" colSpan={3}>Sous-total</td>
                            <td className="text-right px-2 text-purple-700">
                              {formatCurrency(backlog.dossiers.filter(d => (deptView === 'global' || d.departement === deptView) && d.mois_cle === selectedCAMonth).reduce((s, d) => s + (d.montant || 0), 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : <p className="text-sm text-slate-400 italic">Aucun contrat pour ce mois.</p>}
                  </div>
                ) : (
                  /* Vue par mois de production */
                  <div>
                    {/* ── Résumé par département (vue globale) ── */}
                    {deptView === 'global' && (
                      <div className="mb-4 p-3 bg-slate-50 rounded-lg border">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Résumé par département</p>
                        <table className="w-full text-sm">
                          <thead><tr className="border-b text-xs text-slate-400">
                            <th className="text-left py-1">Département</th>
                            <th className="text-right py-1">Contrats</th>
                            <th className="text-right py-1">CA</th>
                          </tr></thead>
                          <tbody>
                            {/* PRQC */}
                            {(() => {
                              const prqcDoss = (backlog?.dossiers || []).filter(d => d.departement === 'PRQC');
                              return prqcDoss.length > 0 ? (
                                <tr className="border-b">
                                  <td className="py-1.5 font-medium text-slate-700">🏠 PRQC</td>
                                  <td className="text-right py-1.5">{prqcDoss.length}</td>
                                  <td className="text-right py-1.5 font-semibold">{formatCurrency(prqcDoss.reduce((s, d) => s + (d.montant || 0), 0))}</td>
                                </tr>
                              ) : null;
                            })()}
                            {/* LMDS */}
                            {(() => {
                              const lmdsDoss = (backlog?.dossiers || []).filter(d => d.departement === 'LMDS');
                              return lmdsDoss.length > 0 ? (
                                <tr className="border-b">
                                  <td className="py-1.5 font-medium text-slate-700">🔧 LMDS</td>
                                  <td className="text-right py-1.5">{lmdsDoss.length}</td>
                                  <td className="text-right py-1.5 font-semibold">{formatCurrency(lmdsDoss.reduce((s, d) => s + (d.montant || d.montant_entreprise || 0), 0))}</td>
                                </tr>
                              ) : null;
                            })()}
                            {/* Location */}
                            {(() => {
                              const locDoss = (backlog?.dossiers || []).filter(d => d.departement === 'LOCATION');
                              return locDoss.length > 0 ? (
                                <tr className="border-b">
                                  <td className="py-1.5 font-medium text-slate-700">🚗 Location</td>
                                  <td className="text-right py-1.5">{locDoss.length}</td>
                                  <td className="text-right py-1.5 font-semibold">{formatCurrency(locDoss.reduce((s, d) => s + (d.montant || 0), 0))}</td>
                                </tr>
                              ) : null;
                            })()}
                          </tbody>
                          <tfoot>
                            <tr className="font-bold border-t-2">
                              <td className="py-1.5">Total global</td>
                              <td className="text-right py-1.5">{(backlog?.dossiers || []).length}</td>
                              <td className="text-right py-1.5 text-purple-700">{formatCurrency((backlog?.dossiers || []).reduce((s, d) => s + (d.montant || 0), 0))}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mb-3">Tous les contrats signés (actifs + finalisés + fermés) — Cliquez sur un mois pour le détail</p>
                    {backlog?.par_mois && Object.keys(backlog.par_mois).length > 0 ? (
                      <table className="w-full text-sm">
                        <thead><tr className="border-b bg-slate-50">
                          <th className="text-left py-2 px-2">Mois de production</th>
                          <th className="text-right py-2">Contrats</th>
                          <th className="text-right py-2 px-2">CA</th>
                        </tr></thead>
                        <tbody>
                          {Object.entries(backlog.par_mois || {})
                            .sort(([, a], [, b]) => {
                              if (a.annee !== b.annee) return (a.annee || 9999) - (b.annee || 9999);
                              return (a.mois || 99) - (b.mois || 99);
                            })
                            .map(([cle, info]) => {
                              const dossMois = (backlog.dossiers || []).filter(d => d.mois_cle === cle && (deptView === 'global' || d.departement === deptView));
                              if (dossMois.length === 0) return null;
                              const total = dossMois.reduce((s, d) => s + (d.montant || 0), 0);
                              return (
                                <tr key={cle} className="border-b hover:bg-purple-50 cursor-pointer"
                                  onClick={() => setSelectedCAMonth(cle)}>
                                  <td className="py-2 px-2 text-purple-700 font-medium">{info.label}</td>
                                  <td className="text-right py-2 text-slate-600">{dossMois.length}</td>
                                  <td className="text-right py-2 px-2 font-bold">{formatCurrency(total)}</td>
                                </tr>
                              );
                            })
                          }
                        </tbody>
                        <tfoot>
                          <tr className="font-bold border-t-2">
                            <td className="py-2 px-2">Total CA Sécurisé</td>
                            <td className="text-right py-2">{backlog.dossiers.filter(d => deptView === 'global' || d.departement === deptView).length} contrats</td>
                            <td className="text-right px-2 text-purple-700">{formatCurrency(backlog.dossiers.filter(d => deptView === 'global' || d.departement === deptView).reduce((s, d) => s + (d.montant || 0), 0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : <p className="text-sm text-slate-400">Aucun contrat signé.</p>}

                    {/* 🔧 Diagnostic : mêmes données source, utile ici aussi */}
                    <DiagnosticCASigneCard year={year} navigate={navigate} onClose={() => { setShowCADetail(null); setSelectedCAMonth(null); }} />
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Composant LMDS Backlog Detail (CA Sécurisé — Mois → Semaines) ─── */
function LMDSBacklogDetail({ backlog, selectedCAMonth, setSelectedCAMonth, navigate, onClose }) {
  const MOIS_NOMS = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
  const lmdsDossiers = (backlog?.dossiers || []).filter(d => d.departement === 'LMDS');
  const parMois = {};
  for (const d of lmdsDossiers) { const m = d.mois || 0; if (!parMois[m]) parMois[m] = { mois: m, total_brut: 0, total_entreprise: 0, semaines: [] }; parMois[m].total_brut += d.montant || 0; parMois[m].total_entreprise += d.montant_entreprise || d.montant || 0; parMois[m].semaines.push(d); }
  for (const m in parMois) { parMois[m].semaines.sort((a, b) => (a.semaine || 0) - (b.semaine || 0)); }
  const moisList = Object.values(parMois).sort((a, b) => a.mois - b.mois);
  if (selectedCAMonth) {
    const moisData = parMois[selectedCAMonth];
    return (<div className="space-y-2"><button onClick={() => setSelectedCAMonth(null)} className="text-xs text-blue-600 hover:underline mb-2">← Retour aux mois</button><h3 className="font-semibold text-sm text-slate-700 mb-2">{MOIS_NOMS[selectedCAMonth]} — Detail par semaine</h3>{moisData?.semaines?.length > 0 ? (<table className="w-full text-sm"><thead><tr className="border-b bg-slate-50"><th className="text-left py-2 px-2">Semaine</th><th className="text-left py-2">Installateur</th><th className="text-right py-2">CA brut</th><th className="text-right py-2 px-2">Part entreprise</th></tr></thead><tbody>{moisData.semaines.map((s, i) => (<tr key={s.id || s._id || i} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => { onClose(); navigate(`/sous-traitants/lmds/${s.installateur_id}`); }}><td className="py-2 px-2 text-blue-700 font-medium">S.{s.semaine}</td><td className="py-2">{s.installateur_nom || s.client_nom}</td><td className="py-2 text-right text-slate-600">{formatCurrency(s.montant)}</td><td className="py-2 px-2 text-right font-bold">{formatCurrency(s.montant_entreprise || s.montant)}</td></tr>))}</tbody><tfoot><tr className="font-bold border-t-2"><td className="py-2 px-2" colSpan={2}>Total {MOIS_NOMS[selectedCAMonth]}</td><td className="text-right text-slate-600">{formatCurrency(moisData.total_brut)}</td><td className="text-right px-2 text-purple-700">{formatCurrency(moisData.total_entreprise)}</td></tr></tfoot></table>) : <p className="text-sm text-slate-400 italic">Aucune donnee pour ce mois.</p>}</div>);
  }
  const totalBrut = lmdsDossiers.reduce((s, d) => s + (d.montant || 0), 0);
  const totalEntreprise = lmdsDossiers.reduce((s, d) => s + (d.montant_entreprise || d.montant || 0), 0);
  return (<div className="space-y-2"><p className="text-xs text-slate-500 mb-2">Cliquez sur un mois pour voir le detail par semaine</p><table className="w-full text-sm"><thead><tr className="border-b bg-slate-50"><th className="text-left py-2 px-2">Mois</th><th className="text-right py-2">Nb rapports</th><th className="text-right py-2">CA brut</th><th className="text-right py-2 px-2">Part entreprise</th></tr></thead><tbody>{moisList.map(m => (<tr key={m.mois} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedCAMonth(m.mois)}><td className="py-2 px-2 text-blue-700 font-medium">{MOIS_NOMS[m.mois] || `Mois ${m.mois}`}</td><td className="py-2 text-right text-slate-500">{m.semaines.length}</td><td className="py-2 text-right text-slate-600">{formatCurrency(m.total_brut)}</td><td className="py-2 px-2 text-right font-bold">{formatCurrency(m.total_entreprise)}</td></tr>))}</tbody><tfoot><tr className="font-bold border-t-2"><td className="py-2 px-2" colSpan={2}>Total annuel</td><td className="text-right text-slate-600">{formatCurrency(totalBrut)}</td><td className="text-right px-2 text-purple-700">{formatCurrency(totalEntreprise)}</td></tr></tfoot></table></div>);
}

/* ─── Composant LMDS Production Detail ─── */
function LMDSProductionDetail({ year, selectedCAMonth, setSelectedCAMonth, navigate, onClose }) {
  const [data, setData] = useState(null); const [ldLoading, setLdLoading] = useState(true);
  useEffect(() => { axios.get(`${API_URL}/api/lmds/production-detail/${year}`).then(r => { setData(r.data); setLdLoading(false); }).catch(() => setLdLoading(false)); }, [year]);
  if (ldLoading) return <div className="py-8 text-center text-slate-400">Chargement...</div>;
  if (!data) return <div className="py-8 text-center text-slate-400">Erreur de chargement</div>;
  const MOIS_NOMS = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
  if (selectedCAMonth) {
    const moisData = data.mois?.find(m => m.mois === selectedCAMonth);
    return (<div className="space-y-2"><button onClick={() => setSelectedCAMonth(null)} className="text-xs text-blue-600 hover:underline mb-2">← Retour aux mois</button><h3 className="font-semibold text-sm text-slate-700 mb-2">{MOIS_NOMS[selectedCAMonth]} {year} — Detail par semaine</h3>{moisData?.semaines?.length > 0 ? (<table className="w-full text-sm"><thead><tr className="border-b bg-slate-50"><th className="text-left py-2 px-2">Semaine</th><th className="text-left py-2">Installateur</th><th className="text-right py-2">Contrats</th><th className="text-right py-2">SC</th><th className="text-right py-2">Km</th><th className="text-right py-2 px-2">Total</th></tr></thead><tbody>{moisData.semaines.map((s, i) => (<tr key={s.id || s._id || i} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => { onClose(); navigate(`/sous-traitants/lmds/${s.installateur_id}`); }}><td className="py-2 px-2 text-blue-700 font-medium">S.{s.semaine} <span className="text-xs text-slate-400">({s.date_debut} - {s.date_fin})</span></td><td className="py-2">{s.installateur_nom}</td><td className="py-2 text-right">{s.contrats > 0 ? formatCurrency(s.contrats) : '—'}</td><td className="py-2 text-right">{s.service_calls > 0 ? formatCurrency(s.service_calls) : '—'}</td><td className="py-2 text-right">{s.km > 0 ? formatCurrency(s.km) : '—'}</td><td className="py-2 px-2 text-right font-bold">{formatCurrency(s.total)}</td></tr>))}</tbody><tfoot><tr className="font-bold border-t-2"><td className="py-2 px-2" colSpan={5}>Total {MOIS_NOMS[selectedCAMonth]}</td><td className="text-right px-2 text-emerald-700">{formatCurrency(moisData.total)}</td></tr></tfoot></table>) : <p className="text-sm text-slate-400 italic">Aucun rapport pour ce mois.</p>}</div>);
  }
  return (<div className="space-y-2"><p className="text-xs text-slate-500 mb-2">Cliquez sur un mois pour voir le detail par semaine</p><table className="w-full text-sm"><thead><tr className="border-b bg-slate-50"><th className="text-left py-2 px-2">Mois</th><th className="text-right py-2">Nb semaines</th><th className="text-right py-2 px-2">Total</th></tr></thead><tbody>{data.mois?.map(m => (<tr key={m.mois} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedCAMonth(m.mois)}><td className="py-2 px-2 text-blue-700 font-medium">{m.mois_nom}</td><td className="py-2 text-right text-slate-500">{m.semaines?.length || 0}</td><td className="py-2 px-2 text-right font-bold">{formatCurrency(m.total)}</td></tr>))}</tbody><tfoot><tr className="font-bold border-t-2"><td className="py-2 px-2" colSpan={2}>Total YTD</td><td className="text-right px-2 text-emerald-700">{formatCurrency(data.total_ytd)}</td></tr></tfoot></table></div>);
}

// ─── 🆕 [Phase 30 #5 cleanup] Dialog historique des exécutions du diagnostic CA ──
function HistoriqueDiagnosticDialog({ executions, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="historique-diagnostic-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="historique-diagnostic-dialog"
      >
        <div className="px-4 py-3 border-b flex items-center justify-between bg-slate-50">
          <h3 className="text-sm font-bold text-slate-800">📜 Historique des analyses CA Signé</h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 text-lg"
            data-testid="btn-fermer-historique"
          >✕</button>
        </div>
        <div className="overflow-y-auto p-3 space-y-2">
          {executions.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-4" data-testid="historique-vide">
              Aucune analyse n'a encore été lancée pour ce tableau.
            </p>
          ) : (
            executions.map((exec) => (
              <div
                key={exec.id}
                className="border border-slate-200 rounded-md p-3 hover:bg-slate-50"
                data-testid={`historique-row-${exec.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500">
                      {exec.date_execution ? formatLocalTime(exec.date_execution) : '—'}
                      {' · '}par <strong>{exec.declenchee_par_nom || '—'}</strong>
                      {' · '}<span className="font-mono text-[10px]">{exec.duree_ms || 0} ms</span>
                    </p>
                  </div>
                  <span
                    className={
                      'text-xs font-semibold px-2 py-0.5 rounded ' +
                      ((exec.nombre_clients_retrouves || 0) > 0
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700')
                    }
                  >
                    {(exec.nombre_clients_retrouves || 0) > 0
                      ? `${exec.nombre_clients_retrouves} manquant${exec.nombre_clients_retrouves > 1 ? 's' : ''}`
                      : 'Aucun manquant'}
                  </span>
                </div>
                {(exec.manquantes_snapshot || []).length > 0 && (
                  <details className="text-xs text-slate-600 mt-1">
                    <summary className="cursor-pointer hover:text-slate-800">Voir détail ({exec.manquantes_snapshot.length})</summary>
                    <ul className="mt-2 ml-4 space-y-1 list-disc">
                      {exec.manquantes_snapshot.slice(0, 20).map((m, idx) => (
                        <li key={idx} className="truncate" data-testid="historique-manquante-row">
                          <span className="font-mono text-[10px] mr-1">{m.raison}</span>
                          {m.dossier_id ? (
                            <a
                              href={`/dossier/${m.dossier_id}`}
                              className="text-blue-600 hover:underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {m.client_nom || m.numero_dossier || m.dossier_id}
                            </a>
                          ) : (
                            <span>{m.client_nom || '—'}</span>
                          )}
                          {' · '}{(m.montant ?? 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Diagnostic CA Signé : détecte les contrats "accepté/en attente production" non comptés ──
function DiagnosticCASigneCard({ year, navigate, onClose, tableau = 'ca_total_signe_ytd' }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // 🆕 [Phase 30 #5 cleanup] Historique des exécutions
  const [historique, setHistorique] = useState(null);
  const [historiqueOpen, setHistoriqueOpen] = useState(false);
  const [historiqueLoading, setHistoriqueLoading] = useState(false);

  const runDiagnostic = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/dashboard/diagnostic-ca-signe/${year}?tableau=${tableau}`);
      setReport(res.data);
      setExpanded(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 [Phase 30 #5 cleanup] Charge l'historique des 20 dernières exécutions
  const loadHistorique = async () => {
    if (historiqueLoading) return;
    setHistoriqueLoading(true);
    try {
      const res = await api.get(`/api/dashboard/diagnostic-ca/historique?tableau=${tableau}&annee=${year}&limit=20`);
      setHistorique(res.data?.executions || []);
      setHistoriqueOpen(true);
    } catch (e) {
      console.error(e);
    } finally {
      setHistoriqueLoading(false);
    }
  };

  if (!report) {
    return (
      <div className="mt-4 space-y-2">
        <div className="p-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-700">🔍 Des contrats signés manquent dans le CA ?</p>
              <p className="text-xs text-slate-500">Analyse les contrats {year} (tous statuts post-acceptation : accepté, en attente prod., en production, finalisé, envoyer facture, attente paiement, fermé) non comptés dans le CA. <strong>Cliquez pour lancer l'analyse — elle ne tourne pas automatiquement.</strong></p>
            </div>
            <button
              onClick={runDiagnostic}
              disabled={loading}
              className="px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
              data-testid="btn-diagnostic-ca-signe"
            >
              {loading ? 'Analyse...' : 'Analyser'}
            </button>
          </div>
          {/* 🆕 [Phase 30 #5 cleanup] Lien historique */}
          <button
            type="button"
            onClick={loadHistorique}
            disabled={historiqueLoading}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
            data-testid="btn-voir-historique-diagnostic"
          >
            {historiqueLoading ? 'Chargement…' : '📜 Voir l\'historique des analyses'}
          </button>
        </div>
        {historiqueOpen && (
          <HistoriqueDiagnosticDialog
            executions={historique || []}
            onClose={() => setHistoriqueOpen(false)}
          />
        )}
        <MontantsFantomesCard year={year} navigate={navigate} onClose={onClose} />
      </div>
    );
  }

  const raisons = report.manquantes_raison || {};
  const totalManquantes = Object.values(raisons).reduce((s, arr) => s + (arr?.length || 0), 0);
  const RAISON_LABELS = {
    dossier_signature_obtenue_false: "⚠️ Dossier : signature_obtenue = false",
    dossier_date_signature_hors_annee: "📅 Dossier : date_signature hors de l'année",
    dossier_date_signature_vide: "📅 Dossier : date_signature vide",
    dossier_montant_soumission_zero: "💰 Dossier : montant_soumission = 0",
    dossier_introuvable: "❌ Dossier introuvable",
    soumission_date_signature_vide: "📝 Soumission : date_signature vide",
  };

  return (
    <div className="mt-4 p-3 bg-white border-2 border-blue-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-blue-900">🔍 Diagnostic CA Signé {year}</span>
          <span className="text-xs text-slate-500">— {report.soumissions_dans_annee} contrats acceptés analysés</span>
        </div>
        <button onClick={() => { setReport(null); setExpanded(false); }} className="text-xs text-slate-500 hover:text-slate-700" data-testid="btn-fermer-diagnostic">✕</button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="bg-emerald-50 rounded p-2 text-center">
          <p className="text-[10px] text-emerald-600 uppercase font-semibold">Correctement comptés</p>
          <p className="text-lg font-bold text-emerald-700">{report.deja_comptees?.length || 0}</p>
          <p className="text-[10px] text-emerald-600">{formatCurrency(report.total_ca_compte || 0)}</p>
        </div>
        <div className="bg-red-50 rounded p-2 text-center">
          <p className="text-[10px] text-red-600 uppercase font-semibold">Manquants dans CA</p>
          <p className="text-lg font-bold text-red-700">{totalManquantes}</p>
          <p className="text-[10px] text-red-600">{formatCurrency(report.total_ca_manque || 0)}</p>
        </div>
        <div className="bg-slate-50 rounded p-2 text-center">
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Total soumissions</p>
          <p className="text-lg font-bold text-slate-700">{report.total_soumissions_acceptees || 0}</p>
          <p className="text-[10px] text-slate-500">tous statuts</p>
        </div>
      </div>

      {totalManquantes === 0 ? (
        <p className="text-xs text-center text-emerald-700 font-medium py-2">✅ Tout est correctement compté dans le CA Signé !</p>
      ) : (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left text-xs font-semibold text-blue-700 hover:text-blue-900 py-1"
          >
            {expanded ? '▼' : '▶'} Voir les {totalManquantes} contrat{totalManquantes > 1 ? 's' : ''} manquant{totalManquantes > 1 ? 's' : ''}
          </button>
          {expanded && (
            <div className="space-y-2 mt-2 max-h-80 overflow-y-auto">
              {Object.entries(raisons).map(([raisonKey, lignes]) => {
                if (!lignes || lignes.length === 0) return null;
                return (
                  <div key={raisonKey} className="bg-red-50 border border-red-200 rounded p-2">
                    <p className="text-xs font-bold text-red-900 mb-1">{RAISON_LABELS[raisonKey] || raisonKey} ({lignes.length})</p>
                    <div className="space-y-1">
                      {lignes.map((l) => (
                        <LigneManquante
                          key={l.soumission_id || l.dossier_id}
                          ligne={l}
                          raisonKey={raisonKey}
                          onFixed={runDiagnostic}
                          navigateToDossier={() => { if (onClose) onClose(); navigate(`/crm/projets/${l.dossier_id}`); }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      <MontantsFantomesCard year={year} navigate={navigate} onClose={onClose} />
    </div>
  );
}

export default Dashboard;

// ─── Ligne d'un dossier manquant + éditeur inline pour correction rapide ─────
function LigneManquante({ ligne, raisonKey, onFixed, navigateToDossier }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    signature_obtenue: true,
    date_signature: ligne.date_signature || '',
    montant_soumission: ligne.montant_ht || 0,
    date_debut_production: '',
    date_fin_production: '',
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      // N'envoyer que les champs pertinents selon la raison
      if (raisonKey === 'dossier_signature_obtenue_false') {
        body.signature_obtenue = true;
        if (form.date_signature) body.date_signature = form.date_signature;
      }
      if (raisonKey === 'dossier_date_signature_vide' || raisonKey === 'soumission_date_signature_vide' || raisonKey === 'dossier_date_signature_hors_annee') {
        body.date_signature = form.date_signature;
        body.signature_obtenue = true;
      }
      if (raisonKey === 'dossier_montant_soumission_zero') {
        body.montant_soumission = parseFloat(form.montant_soumission) || 0;
      }
      // Champs production toujours optionnels (permet de réattribuer le mois)
      if (form.date_debut_production) body.date_debut_production = form.date_debut_production;
      if (form.date_fin_production) body.date_fin_production = form.date_fin_production;

      const r = await api.patch(`/api/crm/dossiers/${ligne.dossier_id}/fix-ca-signe`, body);
      if (r.data?.status === 'success') {
        setEditing(false);
        if (onFixed) onFixed();
      }
    } catch (e) {
      alert('Erreur lors de la correction : ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded border border-red-100" data-testid={`ligne-manquante-${ligne.dossier_id}`}>
      <div className="flex items-center justify-between px-2 py-1 text-xs">
        <span className="text-slate-700 flex-1 min-w-0 truncate">
          <strong>{ligne.client_nom || '—'}</strong>
          {ligne.numero_dossier && <span className="text-slate-400 ml-1">({ligne.numero_dossier})</span>}
          <span className="text-slate-400 ml-1">· {ligne.dossier_statut || '?'}</span>
        </span>
        <span className="font-bold text-red-700 mr-2">{formatCurrency(ligne.montant_ht || 0)}</span>
        {ligne.dossier_id && (
          <>
            <button
              onClick={() => setEditing(!editing)}
              className="text-[10px] px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded mr-1"
              data-testid={`btn-corriger-${ligne.dossier_id}`}
            >
              {editing ? '▲' : '✏️ Corriger'}
            </button>
            <button
              onClick={navigateToDossier}
              className="text-[10px] px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded"
              title="Ouvrir le dossier complet"
            >
              ↗
            </button>
          </>
        )}
      </div>

      {editing && (
        <div className="border-t border-red-100 p-2 bg-slate-50 space-y-1.5">
          {/* signature_obtenue (auto si raison le demande) */}
          {(raisonKey === 'dossier_signature_obtenue_false') && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.signature_obtenue}
                onChange={e => setForm({ ...form, signature_obtenue: e.target.checked })}
                data-testid={`input-signature-obtenue-${ligne.dossier_id}`}
              />
              <span className="text-xs">✓ Marquer signature obtenue</span>
            </div>
          )}

          {/* Date signature — toujours utile */}
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-slate-500 w-24 shrink-0">📅 Date signée :</label>
            <input
              type="date"
              value={form.date_signature || ''}
              onChange={e => setForm({ ...form, date_signature: e.target.value })}
              className="flex-1 text-xs border rounded px-1.5 py-0.5"
              data-testid={`input-date-signature-${ligne.dossier_id}`}
            />
          </div>

          {/* Montant si à 0 */}
          {raisonKey === 'dossier_montant_soumission_zero' && (
            <div className="flex items-center gap-2">
              <label className="text-[10px] text-slate-500 w-24 shrink-0">💰 Montant :</label>
              <input
                type="number"
                step="0.01"
                value={form.montant_soumission}
                onChange={e => setForm({ ...form, montant_soumission: e.target.value })}
                className="flex-1 text-xs border rounded px-1.5 py-0.5"
                data-testid={`input-montant-${ligne.dossier_id}`}
              />
              <span className="text-[10px] text-slate-500">$ (avant taxes)</span>
            </div>
          )}

          {/* Dates production — optionnel, pour réattribuer dans la bonne période */}
          <div className="pt-1 border-t border-slate-200">
            <p className="text-[10px] text-slate-500 mb-1">📆 Dates production (optionnel, pour calendrier de prod) :</p>
            <div className="grid grid-cols-2 gap-1.5">
              <input
                type="date"
                placeholder="Début"
                value={form.date_debut_production || ''}
                onChange={e => setForm({ ...form, date_debut_production: e.target.value })}
                className="text-xs border rounded px-1.5 py-0.5"
                data-testid={`input-date-debut-prod-${ligne.dossier_id}`}
              />
              <input
                type="date"
                placeholder="Fin"
                value={form.date_fin_production || ''}
                onChange={e => setForm({ ...form, date_fin_production: e.target.value })}
                className="text-xs border rounded px-1.5 py-0.5"
                data-testid={`input-date-fin-prod-${ligne.dossier_id}`}
              />
            </div>
          </div>

          <div className="flex gap-1 pt-1 justify-end">
            <button
              onClick={() => setEditing(false)}
              className="text-[10px] px-2 py-0.5 text-slate-600 hover:bg-slate-200 rounded"
            >
              Annuler
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="text-[10px] px-3 py-0.5 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50 font-semibold"
              data-testid={`btn-enregistrer-fix-${ligne.dossier_id}`}
            >
              {saving ? '...' : '💾 Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Montants fantômes : lignes du CA qui pointent vers rien ───────────
function MontantsFantomesCard({ year, navigate, onClose }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [cleaning, setCleaning] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const RAISON_LABELS = {
    ajout_orphelin: "🧩 Ajout contrat orphelin (dossier supprimé)",
    dossier_archive: "📦 Dossier archivé mais encore compté",
    client_sans_nom: "❓ Dossier signé sans nom de client",
    montant_suspect_faible: "⚠️ Montant suspect (< 50$)",
  };

  const analyser = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/dashboard/montants-fantomes/${year}`);
      setReport(res.data);
      setExpanded(true);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'analyse : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const nettoyer = async (fantome) => {
    const label = fantome.client_nom && fantome.client_nom !== "—" ? fantome.client_nom : (fantome.dossier_id || "cet élément");
    const action = fantome.source === "ajout_contrat"
      ? `Supprimer définitivement l'ajout orphelin de ${fantome.montant_ttc.toFixed(2)}$ (${label}) ?`
      : `Retirer "${label}" (${fantome.montant_ttc.toFixed(2)}$) du CA Total Signé ? La signature sera annulée et le montant remis à 0.`;
    if (!window.confirm(action)) return;
    setCleaning(fantome.source_id);
    try {
      await api.post(`/api/dashboard/nettoyer-montant-fantome`, {
        source: fantome.source,
        source_id: fantome.source_id,
      });
      await analyser();
    } catch (e) {
      alert("Erreur lors du nettoyage : " + (e?.response?.data?.detail || e.message));
    } finally {
      setCleaning(null);
    }
  };

  if (!report) {
    return (
      <div className="mt-3 p-3 bg-amber-50 border border-dashed border-amber-300 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-amber-900">👻 Des montants fantômes dans les stats ?</p>
            <p className="text-xs text-amber-700">Détecte les lignes qui pointent vers des dossiers inexistants ou archivés.</p>
          </div>
          <button
            onClick={analyser}
            disabled={loading}
            className="px-3 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-md disabled:opacity-50"
            data-testid="btn-analyser-fantomes"
          >
            {loading ? 'Analyse...' : 'Analyser'}
          </button>
        </div>
      </div>
    );
  }

  const fantomes = report.fantomes || [];

  return (
    <div className="mt-3 p-3 bg-white border-2 border-amber-300 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-amber-900">👻 Montants fantômes {year}</span>
          <span className="text-xs text-slate-500">— {report.nb_fantomes} ligne{report.nb_fantomes > 1 ? 's' : ''} détectée{report.nb_fantomes > 1 ? 's' : ''}</span>
        </div>
        <button onClick={() => { setReport(null); setExpanded(false); }} className="text-xs text-slate-500 hover:text-slate-700" data-testid="btn-fermer-fantomes">✕</button>
      </div>

      {fantomes.length === 0 ? (
        <p className="text-xs text-center text-emerald-700 font-medium py-2">✅ Aucun montant fantôme détecté !</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="bg-amber-50 rounded p-2 text-center">
              <p className="text-[10px] text-amber-700 uppercase font-semibold">Lignes fantômes</p>
              <p className="text-lg font-bold text-amber-800">{report.nb_fantomes}</p>
            </div>
            <div className="bg-red-50 rounded p-2 text-center">
              <p className="text-[10px] text-red-700 uppercase font-semibold">Total à retirer</p>
              <p className="text-lg font-bold text-red-700">{formatCurrency(report.total_fantome_ht || 0)}</p>
              <p className="text-[10px] text-red-600">HT ({formatCurrency(report.total_fantome_ttc || 0)} TTC)</p>
            </div>
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left text-xs font-semibold text-amber-800 hover:text-amber-900 py-1"
          >
            {expanded ? '▼' : '▶'} Voir le détail
          </button>

          {expanded && (
            <div className="space-y-2 mt-2 max-h-80 overflow-y-auto">
              {fantomes.map((f) => (
                <div
                  key={f.source_id}
                  className="bg-amber-50 border border-amber-200 rounded p-2 flex items-center justify-between gap-2"
                  data-testid={`fantome-${f.source_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-amber-900 truncate">
                      {RAISON_LABELS[f.raison] || f.raison}
                    </p>
                    <p className="text-[11px] text-slate-700 truncate">
                      <strong>{f.client_nom}</strong>
                      {f.numero_dossier && <span className="text-slate-400 ml-1">({f.numero_dossier})</span>}
                      <span className="text-slate-400 ml-1">· Mois {f.mois} · {f.departement}</span>
                    </p>
                    <p className="text-[10px] text-slate-500 font-mono truncate">ID : {f.dossier_id}</p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    <span className="font-bold text-red-700 text-sm">{formatCurrency(f.montant_ttc)}</span>
                    <button
                      onClick={() => nettoyer(f)}
                      disabled={cleaning === f.source_id}
                      className="text-[10px] px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50 font-semibold"
                      data-testid={`btn-nettoyer-${f.source_id}`}
                    >
                      {cleaning === f.source_id ? '...' : '🗑 Retirer'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

